import { createContext, FC, useContext, useEffect, useReducer } from 'react';
import localForage from 'localforage';
import API from 'services/AxiosConfig';
import { CompanyStateI, CompanyActionTypes, companyInitialState } from 'store';
import { companyReducer } from 'store/company/reducers';

const STATE_LS_KEY = 'company';

export interface Context extends CompanyStateI {
  setLocations: (locations: any[]) => void;
  setApplicantsActivity: (activity: any[]) => void;
  setUsers: (users: any[]) => void;
  setClients: (clients: any[]) => void;
  setCarePartners: (carePartners: any[]) => void;
  setCompanyTypes: (companyTypes: any[]) => void;
  resetCompany: () => void;
  setactivityTypes: (activityTypes: any[]) => void;
  setRatings: (ratings: any[]) => void;
  setCompanyRatings: (ratings: any[]) => void;
  setCareManagerActivities: (activities: any[]) => void;
  setApplicants: (applicants: any[]) => void;
  setAwardTypes: (awardTypes: any[]) => void;
  setDeactivationReasons: (reasons: any[]) => void;
  setLostClientReasons: (reasons: any[]) => void;
  setApplicantStages: (reasons: any[]) => void;
  setReferralPartners: (referralPartners: any[]) => void;
  setProspect: (prospect: any[]) => void;
  setMilestones: (prospect: any[]) => void;
  setLeadSources: (leadSources: any[]) => void;
  setReferralActivityTypes: (referralActivityTypes: any[]) => void;
  refreshLocations: () => Promise<void>;
  refreshUsers: () => Promise<void>;
  refreshClients: () => Promise<void>;
  refreshCarePartners: () => Promise<void>;
  refreshCareManagerActivities: () => Promise<void>;
  refreshApplicants: () => Promise<void>;
  refreshAwardTypes: () => Promise<void>;
  refreshApplicantsActivity: () => Promise<void>;
  refreshDeactivationReasons: () => Promise<void>;
  refreshLostClientReasons: () => Promise<void>;
  refreshApplicantStages: () => Promise<void>;
  refreshReferralPartners: () => Promise<void>;
  refreshCompanies: () => Promise<void>;
  refreshProspect: () => Promise<void>;
  refreshAll: () => Promise<void>;
  setCompanies: (companies: any[]) => void;
  refreshMilestones: () => Promise<void>;
  refreshRatings: () => Promise<void>;
  refreshCompanyRatings: () => Promise<void>;
  refreshactivityTypes: () => Promise<void>;
  refreshCompanyTypes: () => Promise<void>;
  refreshLeadSources: () => Promise<void>;
  refreshReferralActivityTypes: () => Promise<void>;
  refreshMap: Record<string, () => Promise<void>>;
}

export const CompanyContext = createContext({} as Context);

export const CompanyContextProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(companyReducer, companyInitialState);

  useEffect(() => {
    const loadState = async () => {
      const storedState = await localForage.getItem<CompanyStateI>(STATE_LS_KEY);
      if (storedState) {
        dispatch({ type: CompanyActionTypes.LOAD_STATE, payload: storedState });
      }
    };
    loadState();
  }, []);

  useEffect(() => {
    localForage.setItem(STATE_LS_KEY, state);
  }, [state]);

  const setLocations = (locations: any[]) => dispatch({ type: CompanyActionTypes.SET_LOCATIONS, payload: locations });
  const setUsers = (users: any[]) => dispatch({ type: CompanyActionTypes.SET_USERS, payload: users });
  const setClients = (clients: any[]) => dispatch({ type: CompanyActionTypes.SET_CLIENTS, payload: clients });
  const setCarePartners = (carePartners: any[]) => dispatch({ type: CompanyActionTypes.SET_CAREPARTNERS, payload: carePartners });
  const setCareManagerActivities = (activities: any[]) => dispatch({ type: CompanyActionTypes.SET_CARE_MANAGER_ACTIVITIES, payload: activities });
  const setApplicants = (applicants: any[]) => dispatch({ type: CompanyActionTypes.SET_APPLICANTS, payload: applicants });
  const setAwardTypes = (awardTypes: any[]) => dispatch({ type: CompanyActionTypes.SET_AWARD_TYPES, payload: awardTypes });
  const setDeactivationReasons = (reasons: any[]) => dispatch({ type: CompanyActionTypes.SET_DEACTIVATION_REASONS, payload: reasons });
  const setLostClientReasons = (reasons: any[]) => dispatch({ type: CompanyActionTypes.SET_LOST_CLIENT_REASONS, payload: reasons });
  const setApplicantStages = (reasons: any[]) => dispatch({ type: CompanyActionTypes.SET_APPLICANT_STAGES, payload: reasons });
  const setactivityTypes = (activityTypes: any[]) => dispatch({ type: CompanyActionTypes.SET_ACTIVITY_TYPES, payload: activityTypes });
  const setApplicantsActivity = (applicantsActivity: any[]) => dispatch({ type: CompanyActionTypes.SET_APPLICANT_ACTIVITY, payload: applicantsActivity });
  const setLeadSources = (leadSources: any[]) => dispatch({ type: CompanyActionTypes.SET_LEAD_SOURCES, payload: leadSources });
  const setReferralPartners = (referralPartners: any[]) => dispatch({ type: CompanyActionTypes.SET_REFERRAL_PARTNERS, payload: referralPartners });
  const setCompanies = (companies: any[]) => dispatch({ type: CompanyActionTypes.SET_COMPANIES, payload: companies });
  const setProspect = (prospect: any[]) => dispatch({ type: CompanyActionTypes.SET_PROSPECT, payload: prospect });
  const setMilestones = (milestones: any[]) => dispatch({ type: CompanyActionTypes.SET_MILESTONES, payload: milestones });
  const setRatings = (ratings: any[]) => dispatch({ type: CompanyActionTypes.SET_RATINGS, payload: ratings });
  const setCompanyRatings = (ratings: any[]) => dispatch({ type: CompanyActionTypes.SET_COMPANY_RATINGS, payload: ratings });
  const setCompanyTypes = (companyTypes: any[]) => dispatch({ type: CompanyActionTypes.SET_COMPANY_TYPES, payload: companyTypes });
  const setReferralActivityTypes = (referralActivityTypes: any[]) => dispatch({ type: CompanyActionTypes.SET_REFERRAL_ACTIVITY_TYPES, payload: referralActivityTypes });

  const resetCompany = async () => {
    await localForage.removeItem(STATE_LS_KEY);
    dispatch({ type: CompanyActionTypes.RESET });
  };

  const createRefresher = (apiCall: () => Promise<any[]>, setter: (data: any[]) => void) => async () => {
    const data = await apiCall();
    setter(data);
  };

  const refreshLocations = createRefresher(async () => (await API.get('location/all?type=Active')).data.data, setLocations);
  const refreshUsers = createRefresher(async () => (await API.get('user/Active')).data.data, setUsers);
  const refreshClients = createRefresher(async () => (await API.get('client/ddn/0')).data.data, setClients);
  const refreshCarePartners = createRefresher(async () => (await API.get('care-partner/ddn/0')).data.data, setCarePartners);
  const refreshCareManagerActivities = createRefresher(async () => (await API.get('care-manager-activity')).data.data, setCareManagerActivities);
  const refreshApplicants = createRefresher(async () => (await API.get('applicants/ddn/0')).data.data, setApplicants);
  const refreshAwardTypes = createRefresher(async () => (await API.get('awards/ddn/Active')).data.data, setAwardTypes);
  const refreshDeactivationReasons = createRefresher(async () => (await API.get('client/activity/search/Active')).data.data, setDeactivationReasons);
  const refreshLostClientReasons = createRefresher(async () => (await API.get('care-partner/activity/search/Active')).data.data, setLostClientReasons);
  const refreshApplicantStages = createRefresher(async () => (await API.get('applicants/stages')).data.data, setApplicantStages);
  const refreshReferralPartners = createRefresher(async () => (await API.get('referral-partners/ddn/0')).data.data, setReferralPartners);
  const refreshCompanies = createRefresher(async () => (await API.get('referral-partners/companies/ddn/0')).data.data, setCompanies);
  const refreshProspect = createRefresher(async () => (await API.get('/prospects/ddn/all')).data.data, setProspect);
  const refreshMilestones = createRefresher(async () => (await API.get('/prospects/progress')).data.data, setMilestones);
  const refreshRatings = createRefresher(async () => (await API.get('/rating-referral/activity/search/Active?referral=true')).data.data, setRatings);
  const refreshCompanyRatings = createRefresher(async () => (await API.get('/rating-referral-company/activity/search/Active?referral=true')).data.data, setCompanyRatings);
  const refreshactivityTypes = createRefresher(async () => (await API.get(`marketing/types?referral=false`)).data.data, setactivityTypes);
  const refreshCompanyTypes = createRefresher(async () => (await API.get(`referral-partners/types/listing`)).data.data, setCompanyTypes);
  const refreshApplicantsActivity = createRefresher(async () => (await API.get('applicants/activity')).data.data, setApplicantsActivity);
  const refreshLeadSources = createRefresher(async () => (await API.get('/lead-source/activity/search/Active')).data.data, setLeadSources);
  const refreshReferralActivityTypes = createRefresher(async () => (await API.get(`marketing/types?referral=true`)).data.data, setReferralActivityTypes);
  // Add this after refreshReferralActivityTypes
  const refreshMap: Record<string, () => Promise<void>> = {
    locations: refreshLocations,
    users: refreshUsers,
    clients: refreshClients,
    carePartners: refreshCarePartners,
    careManagerActivities: refreshCareManagerActivities,
    applicants: refreshApplicants,
    awardTypes: refreshAwardTypes,
    deactivationReasons: refreshDeactivationReasons,
    lostClientReasons: refreshLostClientReasons,
    applicantStages: refreshApplicantStages,
    referralPartners: refreshReferralPartners,
    companies: refreshCompanies,
    prospects: refreshProspect,
    milestones: refreshMilestones,
    ratings: refreshRatings,
    companyRatings: refreshCompanyRatings,
    activityTypes: refreshactivityTypes,
    companyTypes: refreshCompanyTypes,
    applicantActivities: refreshApplicantsActivity,
    leadSources: refreshLeadSources,
    referralActivityTypes: refreshReferralActivityTypes
  };

  const refreshAll = async () => {
    await Promise.all([
      refreshLocations(),
      refreshUsers(),
      refreshClients(),
      refreshCarePartners(),
      refreshCareManagerActivities(),
      refreshApplicants(),
      refreshAwardTypes(),
      refreshDeactivationReasons(),
      refreshLostClientReasons(),
      refreshApplicantStages(),
      refreshReferralPartners(),
      refreshCompanies(),
      refreshProspect(),
      refreshMilestones(),
      refreshRatings(),
      refreshCompanyRatings(),
      refreshactivityTypes(),
      refreshCompanyTypes(),
      refreshApplicantsActivity(),
      refreshLeadSources(),
      refreshReferralActivityTypes(),
    ]);
  };


  return (
    <CompanyContext.Provider
      value={{
        ...state,
        setLocations,
        setRatings,
        setCompanyRatings,
        setUsers,
        setClients,
        setCarePartners,
        setCareManagerActivities,
        setApplicants,
        setAwardTypes,
        setMilestones,
        setCompanyTypes,
        setactivityTypes,
        setApplicantsActivity,
        setLeadSources,
        setDeactivationReasons,
        setLostClientReasons,
        setApplicantStages,
        setReferralPartners,
        setCompanies,
        setProspect,
        setReferralActivityTypes,
        resetCompany,
        refreshLocations,
        refreshUsers,
        refreshClients,
        refreshCarePartners,
        refreshCareManagerActivities,
        refreshApplicants,
        refreshAwardTypes,
        refreshDeactivationReasons,
        refreshLostClientReasons,
        refreshApplicantStages,
        refreshReferralPartners,
        refreshCompanies,
        refreshProspect,
        refreshAll,
        refreshMilestones,
        refreshRatings,
        refreshCompanyRatings,
        refreshactivityTypes,
        refreshCompanyTypes,
        refreshApplicantsActivity,
        refreshLeadSources,
        refreshReferralActivityTypes,
        refreshMap
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};

export default function useCompany() {
  return useContext(CompanyContext);
}
